import { useState } from "react";
import { motion } from "framer-motion";
import { Github, Code2, ScanLine, ChevronDown, KeyRound } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

const SAMPLE = `# example.py
import psycopg2

STRIPE_KEY = "sk_live_51H8xsamplekey0000000000000"
DB_URL = "postgres://admin:hunter2@db.example.com:5432/prod"

def get_user(conn, user_id):
    cur = conn.cursor()
    cur.execute(f"SELECT * FROM users WHERE id = {user_id}")
    return cur.fetchone()

app.run(debug=True)`;

export function ScanInput({ onScan, loading }) {
  const [tab, setTab] = useState("code");
  const [code, setCode] = useState("");
  const [filename, setFilename] = useState("snippet.py");
  const [repoUrl, setRepoUrl] = useState("");
  const [depth, setDepth] = useState("full");
  const [githubToken, setGithubToken] = useState("");
  const [useAi, setUseAi] = useState(true);

  const submit = () => {
    if (tab === "code") {
      onScan({ mode: "code", code, filename, use_ai: useAi });
    } else {
      onScan({ mode: "repo", repo_url: repoUrl, depth, github_token: githubToken.trim() || undefined, use_ai: useAi });
    }
  };

  const disabled = loading || (tab === "code" ? !code.trim() : !repoUrl.trim());

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="border border-border bg-surface"
      data-testid="scan-input-panel"
    >
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="w-full h-auto p-0 bg-transparent border-b border-border rounded-none grid grid-cols-2">
          <TabsTrigger
            value="code"
            data-testid="tab-paste-code"
            className="rounded-none data-[state=active]:bg-transparent data-[state=active]:text-neon data-[state=active]:shadow-none border-r border-border py-4 font-mono text-xs uppercase tracking-widest data-[state=active]:border-b-2 data-[state=active]:border-b-neon"
          >
            <Code2 className="w-4 h-4 mr-2" /> Paste Code
          </TabsTrigger>
          <TabsTrigger
            value="repo"
            data-testid="tab-github-repo"
            className="rounded-none data-[state=active]:bg-transparent data-[state=active]:text-neon data-[state=active]:shadow-none py-4 font-mono text-xs uppercase tracking-widest data-[state=active]:border-b-2 data-[state=active]:border-b-neon"
          >
            <Github className="w-4 h-4 mr-2" /> GitHub Repo
          </TabsTrigger>
        </TabsList>

        <TabsContent value="code" className="m-0 p-6 space-y-4">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2 font-mono text-xs text-muted-foreground">
              <span>filename:</span>
              <input
                data-testid="filename-input"
                value={filename}
                onChange={(e) => setFilename(e.target.value)}
                className="bg-surface2 border border-border px-2 py-1 text-neon focus:outline-none focus:border-neon transition-colors w-40"
              />
            </div>
            <button
              data-testid="load-sample-btn"
              onClick={() => { setCode(SAMPLE); setFilename("example.py"); }}
              className="font-mono text-xs text-muted-foreground hover:text-neon transition-colors underline underline-offset-4"
            >
              load a vulnerable sample →
            </button>
          </div>
          <textarea
            data-testid="code-textarea"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            spellCheck={false}
            placeholder="// paste your source code here…"
            className="w-full h-64 bg-[#080808] border border-border p-4 font-mono text-sm text-foreground resize-none focus:outline-none focus:border-neon transition-colors placeholder:text-muted-foreground/40"
          />
        </TabsContent>

        <TabsContent value="repo" className="m-0 p-6 space-y-5">
          <div className="flex items-center gap-2 border border-border bg-[#080808] px-3 focus-within:border-neon transition-colors">
            <Github className="w-5 h-5 text-muted-foreground shrink-0" />
            <input
              data-testid="repo-url-input"
              value={repoUrl}
              onChange={(e) => setRepoUrl(e.target.value)}
              placeholder="https://github.com/owner/repository"
              className="w-full bg-transparent py-4 font-mono text-sm text-foreground focus:outline-none placeholder:text-muted-foreground/40"
            />
          </div>
          <div>
            <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Scan depth</p>
            <div className="grid grid-cols-2 gap-3">
              {[
                { k: "common", t: "Common files", d: "Fast — configs, servers, routes, migrations" },
                { k: "full", t: "Full recursive", d: "Thorough — every source file in the repo" },
              ].map((o) => (
                <button
                  key={o.k}
                  data-testid={`depth-${o.k}`}
                  onClick={() => setDepth(o.k)}
                  className={`text-left p-3 border transition-colors ${depth === o.k ? "border-neon bg-neon/5" : "border-border hover:border-muted-foreground"}`}
                >
                  <span className={`font-mono text-xs uppercase tracking-wide ${depth === o.k ? "text-neon" : "text-foreground"}`}>{o.t}</span>
                  <span className="block text-[11px] text-muted-foreground mt-1">{o.d}</span>
                </button>
              ))}
            </div>
          </div>
          <div>
            <div className="flex items-center gap-2 mb-2">
              <KeyRound className="w-3.5 h-3.5 text-muted-foreground" />
              <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">GitHub token <span className="text-muted-foreground/60">(optional)</span></span>
            </div>
            <input
              data-testid="github-token-input"
              type="password"
              value={githubToken}
              onChange={(e) => setGithubToken(e.target.value)}
              placeholder="ghp_… — lifts rate limits & scans private repos"
              className="w-full bg-[#080808] border border-border px-3 py-3 font-mono text-sm text-neon focus:outline-none focus:border-neon transition-colors placeholder:text-muted-foreground/40"
            />
            <p className="font-mono text-[11px] text-muted-foreground mt-2">
              Public repos work without a token, but GitHub limits unauthenticated scans to ~60/hour. A read-only classic token raises that to 5,000/hour and unlocks private repos. Your token is used for this scan only and never stored.
            </p>
          </div>
        </TabsContent>
      </Tabs>

      <div className="flex items-center justify-between gap-4 border-t border-border p-6 flex-wrap">
        <label className="flex items-center gap-2 cursor-pointer select-none" data-testid="use-ai-toggle">
          <input
            type="checkbox"
            checked={useAi}
            onChange={(e) => setUseAi(e.target.checked)}
            className="peer sr-only"
          />
          <span className={`w-9 h-5 border flex items-center transition-colors ${useAi ? "border-neon bg-neon/20" : "border-border"}`}>
            <span className={`w-3.5 h-3.5 m-0.5 transition-transform ${useAi ? "bg-neon translate-x-4" : "bg-muted-foreground"}`} />
          </span>
          <span className="font-mono text-xs text-muted-foreground">AI deep analysis <span className="text-neon">(Claude)</span></span>
        </label>

        <button
          data-testid="run-scan-btn"
          onClick={submit}
          disabled={disabled}
          className="group inline-flex items-center gap-2 bg-neon text-black font-mono font-bold uppercase tracking-widest text-sm px-8 py-3 disabled:opacity-30 disabled:cursor-not-allowed hard-shadow-hover"
        >
          <ScanLine className="w-4 h-4" />
          {loading ? "Scanning…" : "Run Scan"}
        </button>
      </div>
    </motion.div>
  );
}
